import { useEffect } from "react";
import {
  Modal,
  Title,
  Text,
  Divider,
  Paper,
  List,
  Stack,
  Accordion,
  Grid,
  Group,
  Badge,
  Loader,
  Center,
} from "@mantine/core";
import { IconChevronDown,IconCalendar,IconUser, IconArrowDown } from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { getAllObjectives } from "../actions/improvementPlanActions";
import { ATTRIBUTE_TYPES } from "../enums/AttributesTypes";
import { IconTrendingUp, IconTrendingDown } from "@tabler/icons-react";
import LabeledBadge from "./LabeledBadge";

const ImprovementPlanModal = ({ opened, onClose, idEvaluacion, planData = null, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const {data:improvementPlans, loading} = useSelector((state) => state.improvementPlan.objectives);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (opened && idEvaluacion && !planData) {
      dispatch(getAllObjectives(idEvaluacion, user));
    }
  }, [opened, idEvaluacion, dispatch, planData]);

  const plansToShow = planData ? [planData] : (improvementPlans || []);
  const isLoading = planData ? false : loading;
  const modalTitle = isReadOnly ? "Plan de Mejora - Etapa Anterior" : "Plan de Mejora";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} size="xl" >{modalTitle}</Text>}
      size="lg"
      radius="md"
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 2,
      }}
    >
      {isLoading ? (
        <Center>
          <Loader size="lg" type="dots" color="#E72166"/>
        </Center>
      ) : plansToShow?.length > 0 ? (
        plansToShow.map((plan, index) => (
          <Paper
            key={index}
            shadow="md"
            radius="md"
            p="md"
            mb="lg"
            withBorder
            style={{ backgroundColor: "#fcfcfc" }}
          >
            <Stack spacing="xs">
              <Title order={3} fw={450} c={'#E72166'}>
                Detalles del {plan.titulo || "Sin título"} {plan.numero}
              </Title>
              <Divider size="xs" />
              <Text fw={500} size="1rem">Descripción: </Text>
              <Text size="sm" ml={2}> {plan.descripcion || "Sin descripción"} </Text>
              <Divider size="xs" />
              { plan.cualidades?.length > 0 && (
                <>
                  <Text fw={500} size="1rem"> Debilidades / Fortalezas </Text>
                  <List spacing="xs" size="sm">
                    {plan.cualidades.map((attr, index) => (
                      <List.Item
                        key={index}
                        icon={
                          attr?.tipoAtributo?.Id.toUpperCase() === ATTRIBUTE_TYPES.FORTALEZA ? 
                          (
                            <IconTrendingUp color="green" size={20} />
                          ) : (
                            <IconTrendingDown color="red" size={20} />
                          ) 
                        }
                      >
                        {attr.descripcion}
                      </List.Item>
                    ))}
                  </List>  
                </>
              )}
              <Divider size="xs" />
              <Text fw={500} size="1rem"> Estrategias: </Text>
              { plan.estrategias?.length > 0 && (
                <Accordion
                  chevron={<IconArrowDown color="#E72166"/>}
                  chevronSize={25}
                  variant="separated"
                  defaultValue=""
                >
                  {plan.estrategias.map((estrategia, index) => (
                    <Accordion.Item value={estrategia.Id} key={index} style={{ borderLeft: "5px solid #E72166",}}>
                      <Accordion.Control>
                        <Text fw={600}> {index + 1}. {estrategia.descripcion} </Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        {estrategia.metas?.length > 0 && ( 
                          <>
                            <Group position="apart" mb="xs" mt="md">
                              <Text fw={600} size="md"> Metas </Text>
                            </Group>
                            <Grid gutter="sm">
                              {estrategia.metas.map((meta, index) => (
                                <Grid.Col key={index} span={12}>
                                  <Paper p="sm" shadow="xs" radius="md" >
                                    <Stack gap={'xs'}>
                                      <LabeledBadge
                                        icon={null}
                                        label="Título:"
                                        description={meta.titulo}
                                      />
                                      <LabeledBadge
                                        icon={null}
                                        label="Descripción:"
                                        description={meta.descripcion}
                                      />
                                      <LabeledBadge
                                        icon={<IconCalendar size={14} />}
                                        label="Vence:"
                                        description={new Date(meta.fechaVencimiento).toLocaleDateString()}
                                      />
                                    </Stack>
                                  </Paper>
                                </Grid.Col>
                              ))}
                            </Grid>
                          </>
                        )}
                        {estrategia.recursos?.length > 0 && (
                          <>
                            <Group position="apart" mb="xs" mt="md">
                              <Text fw={600} size="md" > Recursos </Text>
                            </Group>
                            <Stack spacing="sm">
                              {estrategia.recursos.map((recurso, index) => (
                                <Paper 
                                  key={index}
                                  p="sm" 
                                  shadow="xs"
                                  radius="md"
                                > 
                                  <Stack gap={'xs'}>
                                    <LabeledBadge
                                      icon={null}
                                      label="Título:"
                                      description={recurso.titulo}
                                    />
                                    <LabeledBadge
                                      icon={null}
                                      label="Descripción:"
                                      description={recurso.descripcion}
                                    />
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                          </>
                        )}
                        {estrategia.responsables?.length > 0 && ( 
                          <>
                            <Group position="apart" mb="xs" mt="md">
                              <Text fw={600} size="md" > Responsables </Text>
                            </Group>
                            <Stack spacing="xs">
                              {estrategia.responsables.map((resp,index) => (
                                <Paper 
                                  key={index}
                                  p="sm" 
                                  shadow="xs"
                                  radius="md"
                                  withBorder
                                  style={{ borderTop: '2px solid #e0d6ff' }}
                                >
                                  <Stack gap={'xs'}>
                                    <LabeledBadge
                                      icon={<IconUser size={12} />}
                                      label="Responsable:"
                                      description={resp.usuarioMail}
                                    />
                                    <LabeledBadge
                                      icon={null}
                                      label="Responsabilidad:"
                                      description={resp.descripcion}
                                    />
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                          </>
                        )}
                      </Accordion.Panel>
                    </Accordion.Item> 
                  ))}
                </Accordion> 
              )}
            </Stack>
          </Paper> 
        ))
      ) : (
        <Text c="dimmed">No hay planes de mejora para esta evaluación.</Text>
      )}
    </Modal>  
  );  
};

export default ImprovementPlanModal;
